// Hook to gather all dashboard context for agent integration
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProviderUnified';

import { useScheduleContext } from './ScheduleProvider';
import { logger } from '../lib/logger';

let externalDashboardContext = {};

export function updateExternalDashboardContext(partial) {
  try {
    externalDashboardContext = {
      ...externalDashboardContext,
      ...partial,
    };
  } catch (_e) {
    // no-op
  }
}

export const useDashboardContext = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addEvent, schedule } = useScheduleContext();

  const placeholderFunctions = useMemo(
    () => ({
      incidents: [],
      updateIncident: (id, data) => {
        logger.warn('[DashboardContext] updateIncident not wired', { id, data });
      },
      addIncident: (incident) => {
        logger.warn('[DashboardContext] addIncident not wired', { incident });
      },

      workers: [],
      updateWorker: (id, data) => {
        logger.warn('[DashboardContext] updateWorker not wired', { id, data });
      },

      addTrainingAssignment: (assignment) => {
        logger.warn('[DashboardContext] addTrainingAssignment not wired', { assignment });
      },

      showNotification: (notification) => {
        logger.info('[DashboardContext] showNotification', { notification });
        if (window.showToast) {
          window.showToast(notification.message, notification.type);
        }
      },
      broadcastMessage: (message) => {
        logger.info('[DashboardContext] broadcastMessage', { message });
      },

      setDashboardFilter: (filter) => {
        logger.info('[DashboardContext] setDashboardFilter', { filter });
        localStorage.setItem('dashboardFilter', JSON.stringify(filter));
      },
      updateView: (viewConfig) => {
        logger.info('[DashboardContext] updateView', { viewConfig });
      },

      metrics: {},
    }),
    []
  );

  // Combine real and mock context
  const dashboardContext = useMemo(() => {
    // Use schedule as events for compatibility
    const events = schedule || [];

    const base = {
      // Real functions from hooks
      user,
      navigate,
      addEvent,
      events,

      // Placeholder functions (no mock data)
      ...placeholderFunctions,

      // Additional context
      tenantId: user?.tenantId || user?.organizationId || null,
      companyId: user?.companyId || user?.organizationId || null,

      // Helper to check if a feature is available
      hasFeature: (feature) => {
        const availableFeatures = ['schedule', 'navigation', 'reports'];
        return availableFeatures.includes(feature);
      },
    };

    // Merge external context (from dashboards) with base
    if (!externalDashboardContext || Object.keys(externalDashboardContext).length === 0) {
      return base;
    }

    return {
      ...base,
      ...externalDashboardContext,
      // Merge nested structures we care about explicitly
      metrics: {
        ...(base.metrics || {}),
        ...(externalDashboardContext.metrics || {}),
      },
      // Core data from all dashboard pages
      workers: externalDashboardContext.workers || base.workers,
      workerMetrics: externalDashboardContext.workerMetrics || {},
      incidents: externalDashboardContext.incidents || base.incidents,
      incidentMetrics: externalDashboardContext.incidentMetrics || {},
      hazards: externalDashboardContext.hazards || [],
      hazardMetrics: externalDashboardContext.hazardMetrics || {},
      equipment: externalDashboardContext.equipment || [],
      equipmentStats: externalDashboardContext.equipmentStats || {},
      equipmentMetrics: externalDashboardContext.equipmentMetrics || {},
      projects: externalDashboardContext.projects || externalDashboardContext.jobsites || [],
      jobsites: externalDashboardContext.jobsites || externalDashboardContext.projects || [],
      complianceRequirements: externalDashboardContext.complianceRequirements || [],
      compliance: externalDashboardContext.compliance || {},
      schedule: externalDashboardContext.schedule || events,
      scheduleMetrics: externalDashboardContext.scheduleMetrics || {},
      documents: externalDashboardContext.documents || [],
      documentMetrics: externalDashboardContext.documentMetrics || {},
      evidence: externalDashboardContext.evidence || [],
      evidenceMetrics: externalDashboardContext.evidenceMetrics || {},
      integrations: externalDashboardContext.integrations || {},
      analytics: externalDashboardContext.analytics || {},
      intelligentActions: externalDashboardContext.intelligentActions || [],
      actionStats: externalDashboardContext.actionStats || {},
      agentSummary: externalDashboardContext.agentSummary || null,
      directory: externalDashboardContext.directory || [],
      riskBreakdown: externalDashboardContext.riskBreakdown || [],
    };
  }, [user, navigate, addEvent, schedule, placeholderFunctions]);

  return dashboardContext;
};

// Hook to inject dashboard context into a specific service
export const useInjectDashboardContext = (service) => {
  const context = useDashboardContext();

  const inject = useCallback(() => {
    if (service && typeof service.setDashboardContext === 'function') {
      service.setDashboardContext(context);
    } else if (service && typeof service.updateContext === 'function') {
      service.updateContext(context);
    }
  }, [service, context]);

  return inject;
};
